<template>
  <div class="week-context-row">
    <div class="week-context-row__schedule">
      <span class="week-context-row__label">{{ hasExactSchedule ? "Post on" : props.weekLabel }}</span>
      <span class="week-context-row__value">{{ hasExactSchedule ? exactScheduleLabel : selectedWeekLabel }}</span>
      <span class="week-context-row__picker-anchor">
        <LinkWithIcon class="week-context-row__edit" color="gray" isNoUnderline :aria-label="hasExactSchedule ? 'Change exact schedule' : 'Change week'" @click="openDisplayedSchedulePicker">
          <template #icon><IconEmbedded name="pencil_2" :size="15" /></template>
        </LinkWithIcon>
        <VueDatePicker
          ref="weekPickerRef"
          v-model="selectedWeekRange"
          class="week-context-row__picker"
          weekPicker
          :minDate="exactScheduleMinDate"
          :maxDate="exactScheduleMaxDate"
          preventMinMaxNavigation
          :weekStart="0"
          :enableTimePicker="false"
          autoApply
          teleport="body"
          :config="{ keepActionRow: props.allowExactSchedule }"
          @update:modelValue="handleWeekSelection"
        >
          <template #action-extra>
            <div v-if="props.allowExactSchedule" class="exact-schedule-action">
              <span>We'll pick a day &amp; time, </span>
              <button type="button" @click.stop="openExactSchedulePicker">or choose yourself</button>
            </div>
          </template>
          <template #action-preview><span aria-hidden="true"></span></template>
        </VueDatePicker>
        <VueDatePicker
          v-if="props.allowExactSchedule"
          ref="exactSchedulePickerRef"
          v-model="exactScheduleLocalDateTime"
          class="week-context-row__picker"
          :minDate="exactScheduleMinDate"
          :maxDate="exactScheduleMaxDate"
          :weekStart="0"
          :is24="false"
          timePickerInline
          teleport="body"
          :config="{ setDateOnMenuClose: true, keepActionRow: true }"
          @update:modelValue="handleExactScheduleSelection"
        />
      </span>
      <LinkWithIcon v-if="hasExactSchedule" color="gray" isNoUnderline aria-label="Use automatic scheduling" @click="clearExactSchedule">
        <template #icon><IconEmbedded name="remove_4-5" :size="13" /></template>
      </LinkWithIcon>
    </div>
    <slot />
  </div>
  <div v-if="showSuggestedSchedule" class="suggested-schedule" :title="props.suggestedReason || undefined">
    <span class="suggested-schedule__text">- Scheduled {{ suggestedScheduleLabel }}</span>
    <button type="button" class="suggested-schedule__action" @click="useSuggestedSchedule">change</button>
  </div>
  <div v-if="props.validationError" class="global-error-string exact-schedule-error">{{ props.validationError }}</div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from "vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import DayjsExtended from "@logic/DayjsExtended";
import { getPostScheduleMaxDate, getPostScheduleMinDate } from "@logic/ContentCalendarValidator";
import { createContentCalendarWeekRange, formatContentCalendarWeekRange, type ContentCalendarWeekRange } from "@logic/ContentCalendarWeek";

const props = withDefaults(
  defineProps<{
    customerTimeZone?: string | null;
    allowExactSchedule?: boolean;
    weekLabel?: string;
    showWeekStartOnly?: boolean;
    validationError?: string;
    /** Predicted schedule for this week ("yyyy-MM-ddTHH:mm" local), shown until the user picks an exact time. */
    suggestedLocalDateTime?: string | null;
    /** Plain-language explanation of the suggestion, shown as a tooltip. */
    suggestedReason?: string;
  }>(),
  {
    customerTimeZone: null,
    allowExactSchedule: true,
    weekLabel: "Week:",
    showWeekStartOnly: false,
    validationError: "",
    suggestedLocalDateTime: null,
    suggestedReason: "",
  }
);

const selectedWeekRange = defineModel<ContentCalendarWeekRange | null>("selectedWeekRange", { required: true });
const exactScheduleLocalDateTime = defineModel<Date | string | null>("exactScheduleLocalDateTime", { required: true });

const weekPickerRef = ref<{ openMenu: () => void; closeMenu: () => void } | null>(null);
const exactSchedulePickerRef = ref<{ openMenu: () => void } | null>(null);
const hasExactSchedule = computed<boolean>(() => props.allowExactSchedule && !!exactScheduleLocalDateTime.value);
const selectedWeekLabel = computed<string>(() => formatContentCalendarWeekRange(selectedWeekRange.value, { startOnly: props.showWeekStartOnly }));
const exactScheduleLabel = computed<string>(() => formatScheduleLabel(exactScheduleLocalDateTime.value));
const exactScheduleMinDate = computed<string>(() => getPostScheduleMinDate(props.customerTimeZone));
const exactScheduleMaxDate = computed<string>(() => getPostScheduleMaxDate(props.customerTimeZone));
const suggestedScheduleLabel = computed<string>(() => formatScheduleLabel(props.suggestedLocalDateTime));
// Once the user has chosen an exact time there's nothing to preview, so the line simply disappears.
const showSuggestedSchedule = computed<boolean>(() => props.allowExactSchedule && !hasExactSchedule.value && !!suggestedScheduleLabel.value);

function formatScheduleLabel(value: Date | string | null | undefined): string {
  if (!value) {
    return "";
  }
  const dt = DayjsExtended(value);
  return dt.format(dt.minute() === 0 ? "ddd, MMM D [at] ha" : "ddd, MMM D [at] h:mma");
}

function openDisplayedSchedulePicker() {
  if (hasExactSchedule.value) {
    exactSchedulePickerRef.value?.openMenu();
    return;
  }
  weekPickerRef.value?.openMenu();
}

async function openExactSchedulePicker() {
  weekPickerRef.value?.closeMenu();
  await nextTick();
  exactSchedulePickerRef.value?.openMenu();
}

/** Pre-fill the exact picker with the suggested time so the user starts from it instead of an empty picker. */
async function useSuggestedSchedule() {
  if (!props.suggestedLocalDateTime) {
    return;
  }
  exactScheduleLocalDateTime.value = DayjsExtended(props.suggestedLocalDateTime).toDate();
  // openExactSchedulePicker closes the week menu first, so set the value before opening it.
  await nextTick();
  await openExactSchedulePicker();
}

function handleWeekSelection() {
  exactScheduleLocalDateTime.value = null;
}

function handleExactScheduleSelection(value: Date | string | null) {
  if (!value) {
    return;
  }
  selectedWeekRange.value = createContentCalendarWeekRange(DayjsExtended(value).format("YYYY-MM-DD"));
}

function clearExactSchedule() {
  exactScheduleLocalDateTime.value = null;
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

.week-context-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 6px;
  position: relative;
  font-size: 14px;

  &__schedule {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-top: 8px;
    min-width: 0;
  }

  &__label {
    color: #333;
    font-weight: 600;
  }

  &__value {
    white-space: nowrap;
  }

  &__edit {
    margin-left: 1px;
  }

  &__picker-anchor {
    display: inline-flex;
    position: relative;
  }

  &__picker {
    width: 1px;
    height: 1px;
    position: absolute;
    left: 50%;
    bottom: 0;
    overflow: hidden;
    opacity: 0;
    pointer-events: none;
  }
}

@media (max-width: $mobile-max-width) {
  .week-context-row {
    align-items: flex-start;
  }
}

.exact-schedule-action {
  display: block;
  width: 100%;
  padding: 8px 4px 10px;
  box-sizing: border-box;
  color: #666;
  font-size: 13px;
  line-height: 1.4;
  text-align: left;

  button {
    display: inline;
    padding: 0;
    border: 0;
    color: #118689;
    background: transparent;
    cursor: pointer;
    font: inherit;
    text-decoration: underline;
    text-decoration-style: dotted;
    text-underline-offset: 2px;
  }
}

.suggested-schedule {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0 5px;
  margin-top: 5px;
  margin-left: 5px;
  color: #666;
  font-size: 13px;
  line-height: 1.4;

  &__action {
    display: inline;
    padding: 0;
    border: 0;
    color: #666;
    background: transparent;
    cursor: pointer;
    font: inherit;
    text-decoration: underline;
    text-decoration-style: dotted;
    text-underline-offset: 2px;

    &:hover {
      text-decoration: none;
    }
  }
}

.exact-schedule-error {
  margin-top: 5px;
}
</style>
